import React, { ComponentProps } from 'react'
import { Link as GatsbyLink } from 'gatsby'
import { clsx } from 'clsx/lite'

const sizes = {
  // Compact — matches a text-ctrl-sm input height (inline forms: newsletter).
  sm: 'px-5 py-2 text-ctrl-sm',
  md: 'px-4 py-1 text-base',
  lg: 'px-5 py-1.5 text-lg sm:text-xl',
}

function isInternal(href: string) {
  return href.startsWith('/') && !href.startsWith('//')
}

type ButtonColor = 'dark/light' | 'light' | 'filled'

const baseClasses = 'inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 font-medium transition-colors'

function colorClasses(color: ButtonColor) {
  switch (color) {
    // Outlined (default) — transparent bg, black border/text. Inverts on hover.
    case 'dark/light':
      return 'border-black bg-transparent text-black hover:bg-black hover:text-white dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-black'
    // Outlined light — white border/text on dark bg. Inverts on hover.
    case 'light':
      return 'border-white bg-transparent text-white hover:bg-white hover:text-black'
    // Filled inverted — solid black bg, white text. Inverts to outline on hover.
    case 'filled':
      return 'border-black bg-black text-white hover:bg-transparent hover:text-black dark:border-white dark:bg-white dark:text-black dark:hover:bg-transparent dark:hover:text-white'
  }
}

// ============================================================================
// Primary buttons
// ============================================================================

export function Button({
  size = 'md',
  type = 'button',
  color = 'dark/light',
  className,
  ...props
}: {
  size?: keyof typeof sizes
  color?: ButtonColor
} & ComponentProps<'button'>) {
  return (
    <button
      type={type}
      className={clsx(baseClasses, colorClasses(color), sizes[size], className)}
      {...props}
    />
  )
}

export function ButtonLink({
  size = 'md',
  color = 'dark/light',
  className,
  href,
  ...props
}: {
  href: string
  size?: keyof typeof sizes
  color?: ButtonColor
} & Omit<ComponentProps<'a'>, 'href' | 'ref'>) {
  const classes = clsx(baseClasses, colorClasses(color), sizes[size], className)
  return isInternal(href) ? (
    <GatsbyLink to={href} className={classes} {...props} />
  ) : (
    <a href={href} className={classes} {...props} />
  )
}

// ============================================================================
// Soft buttons (subtle grey fill)
// ============================================================================

export function SoftButton({
  size = 'md',
  type = 'button',
  className,
  ...props
}: {
  size?: keyof typeof sizes
} & ComponentProps<'button'>) {
  return (
    <button
      type={type}
      className={clsx(
        'inline-flex shrink-0 cursor-pointer items-center justify-center gap-1 rounded-2xl bg-mist-950/10 font-medium text-black hover:bg-mist-950/15 transition-colors dark:bg-white/10 dark:text-white dark:hover:bg-white/20',
        sizes[size],
        className,
      )}
      {...props}
    />
  )
}

export function SoftButtonLink({
  size = 'md',
  href,
  className,
  ...props
}: {
  href: string
  size?: keyof typeof sizes
} & Omit<ComponentProps<'a'>, 'href' | 'ref'>) {
  const classes = clsx(
    'inline-flex shrink-0 cursor-pointer items-center justify-center gap-1 rounded-2xl bg-mist-950/10 font-medium text-black hover:bg-mist-950/15 transition-colors dark:bg-white/10 dark:text-white dark:hover:bg-white/20',
    sizes[size],
    className,
  )
  return isInternal(href) ? (
    <GatsbyLink to={href} className={classes} {...props} />
  ) : (
    <a href={href} className={classes} {...props} />
  )
}

// ============================================================================
// Plain buttons (text only with hover color)
// ============================================================================

export function PlainButton({
  size = 'md',
  color = 'dark/light',
  type = 'button',
  className,
  ...props
}: {
  size?: keyof typeof sizes
  color?: 'dark/light' | 'light'
} & ComponentProps<'button'>) {
  return (
    <button
      type={type}
      className={clsx(
        'inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 font-medium transition-colors',
        color === 'dark/light' && 'text-black hover:text-ctrl-500 dark:text-white dark:hover:text-ctrls',
        color === 'light' && 'text-white hover:text-ctrl-300',
        sizes[size],
        className,
      )}
      {...props}
    />
  )
}

export function PlainButtonLink({
  size = 'md',
  color = 'dark/light',
  href,
  className,
  ...props
}: {
  href: string
  size?: keyof typeof sizes
  // 'inherit' applies no ink preset — the caller supplies text/hover colours
  // via className (e.g. the overlay-aware nav login).
  color?: 'dark/light' | 'light' | 'inherit'
} & Omit<ComponentProps<'a'>, 'href' | 'ref'>) {
  const classes = clsx(
    'inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 font-medium transition-colors',
    color === 'dark/light' && 'text-black hover:text-ctrl-500 dark:text-white dark:hover:text-ctrls',
    color === 'light' && 'text-white hover:text-ctrl-300',
    sizes[size],
    className,
  )
  return isInternal(href) ? (
    <GatsbyLink to={href} className={classes} {...props} />
  ) : (
    <a href={href} className={classes} {...props} />
  )
}
